import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Toolbar,
  AppBar,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { selectSuperAdmin } from "./selectors";
import { connect, useDispatch } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import styles from "./SuperAdmin.styles";
import {
  receiveLabelsListRequest,
  receiveTagsListRequest,
  updateTagRequest,
  updateLabelRequest,
  connectToDriveRequest,
  disconnectDriveRequest,
  checkGoogleDriveConnectionRequest,
} from "./actions";
import eventEmitter, {
  types,
} from "../../common/components/helpers/eventEmitter";
import AccessDeniedPage from "../../containers/access-denied-page/AccessDenied";
import { selectUser } from "../login/selectors";
import { Info } from "@mui/icons-material";
import { filterAllowedChars, isValidLatinName, removeInvalidCharacters } from '../../common/utils';
import { notificationMessage } from '../../common/constants/notication-constants';
import AlertService from '../../common/services/notification.service';
import type { RootState } from '../../reducers';

export interface tagLabelState {
  id: number;
  name: string;
  mongo_id: string;
  created_at: string;
  updated_at: string;
}

export interface Tag extends tagLabelState {}
export interface Label extends tagLabelState {}

export type Tags = Tag[]
export type Labels = Label[]

type EditModeState = {
  isEdit: boolean;
  id: number | null;
};

type ItemDetails = {
  id: number | null;
  name: string;
  is_allowed: boolean;
  modified: boolean;
};

const initialEditModeStateTag: EditModeState = {
  isEdit: false,
  id: null,
};

const initialEditModeStateLabel: EditModeState = {
  isEdit: false,
  id: null,
};

const initialTagDetails: ItemDetails = {
  id: null,
  name: "",
  is_allowed: false,
  modified: false,
};

const initialLabelDetails: ItemDetails = {
  id: null,
  name: "",
  is_allowed: false,
  modified: false,
};

const SuperAdmin = ({ labelsList, tagsList, userRole, driveConnectionCheck }) => {
  // const { labelsList, tagsList, userRole, driveConnectionCheck } = props;
  const safeLabels = Array.isArray(labelsList) ? labelsList: [];
  const safeTags = Array.isArray(tagsList) ? tagsList: [];
  const dispatch = useDispatch();
  const [driveConnection, setDriveConnection] = useState(false);
  const [labels, setLabels] = useState<Labels>(safeLabels);
  const [tags, setTags] = useState<Tags>(safeTags);
  const [editModeTag, setEditModeTag] = useState(initialEditModeStateTag);
  const [editModeLabel, setEditModeLabel] = useState(initialEditModeStateLabel);
  const [editedTagDetails, setEditedTagDetails] = useState(initialTagDetails);
  const [editedLabelDetails, setEditedLabelDetails] =
    useState(initialLabelDetails);
  const [errorLabel, setErrorLabel] = useState<string | null>(null);
  const [errorTag, setErrorTag] = useState<string | null>(null);

  useEffect(() => {
    dispatch(receiveLabelsListRequest());
  }, [dispatch]);

  useEffect(() => {
    dispatch(receiveTagsListRequest());
  }, [dispatch]);

  useEffect(() => {
    setLabels(labelsList);
  }, [dispatch, labelsList]);

  useEffect(() => {
    setTags(tagsList);
  }, [dispatch, tagsList]);

  useEffect(() => {
    dispatch(checkGoogleDriveConnectionRequest());
  }, [dispatch]);

  useEffect(() => {
    setDriveConnection(driveConnectionCheck);
  }, [dispatch, driveConnectionCheck]);

  const handleAddConnection = () => {
    dispatch(connectToDriveRequest());
  };

  const handleRemoveConnection = () => {
    dispatch(disconnectDriveRequest());
  };

  const isDuplicateLabelsTags = (
    currentValue: string,
    list: Tags | Labels,
    currentId: number | null,
  ): boolean=> {
    const value = removeInvalidCharacters(currentValue).toLowerCase();
    return list.some((item) => {
      return item.id !== currentId && removeInvalidCharacters(item.name).toLowerCase() === value;
     }
    );
  };

  const handleSetEditMode = (
    type: string,
    selectedId: number
  ) => {
    if (type === "openLabelEdit") {
      const labelEdit = labels.filter((item: any) => item.id === selectedId);
      setEditModeLabel({ isEdit: !editModeLabel.isEdit, id: selectedId });
      setEditedLabelDetails({
        ...editedLabelDetails,
        name: labelEdit[0].name,
        id: labelEdit[0].id,
      });
    }

    if (type === "saveLabelEdit") {
      const { id, name } = editedLabelDetails;
      const safeName: string = removeInvalidCharacters(name);
      if (!safeName) {
        setErrorLabel("The label name must be filled in!");
        return;
      }
      if (!isValidLatinName(safeName)) {
        setErrorLabel(notificationMessage.ALLOWED_CHARACTERS_ERROR);
        return;
      }
      if (isDuplicateLabelsTags(safeName, labels, id)) {
        AlertService.warning("A label with the same name already exists in the database");
        return;
      }
      dispatch(updateLabelRequest(id, { name: safeName }));
      setEditModeLabel({ isEdit: !editModeLabel.isEdit, id: selectedId });
      setErrorLabel(null);
    }

    if (type === "closeLabelEdit") {
      setEditModeLabel({ isEdit: !editModeLabel.isEdit, id: selectedId });
      setErrorLabel(null);
    }

    if (type === "openTagEdit") {
      const tagEdit = tags.filter((item: any) => item.id === selectedId);
      setEditModeTag({ isEdit: !editModeTag.isEdit, id: selectedId });
      setEditedTagDetails({
        ...editedTagDetails,
        name: tagEdit[0].name,
        id: tagEdit[0].id,
      });
    }

    if (type === "saveTagEdit") {
      const { id, name } = editedTagDetails;
      const safeName: string = removeInvalidCharacters(name);
      if (!safeName) {
        setErrorLabel("The tag name must be filled in!");
        return;
      }
      if (!isValidLatinName(safeName)) {
        setErrorLabel(notificationMessage.ALLOWED_CHARACTERS_ERROR);
        return;
      }

      if (isDuplicateLabelsTags(safeName, tags, id)) {
        AlertService.warning("A tag with the same name already exists in the database");
        return;
      }
      dispatch(updateTagRequest(id, { name }));
      setEditModeTag({ isEdit: !editModeTag.isEdit, id: selectedId });
      setErrorTag(null);
    }

    if (type === "closeTagEdit") {
      setEditModeTag({ isEdit: !editModeTag.isEdit, id: selectedId });
      setErrorTag(null);
    }
  };

  const handleChangeLabel = (
    _fieldName: string,
    selectedId: number,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const raw = event.target.value;
    const filtered =  filterAllowedChars(raw);
    if (raw !== filtered) {
      setErrorLabel(notificationMessage.ALLOWED_CHARACTERS_ERROR);
    } else {
      if (filtered) {
        setErrorLabel(null);
      } else {
        setErrorLabel("The label name must be filled in!")
      }
    }
    setEditedLabelDetails({
      ...editedLabelDetails,
      name: filtered,
      id: selectedId,
      modified: true,
    });
  };

  const handleChangeTag = (
    _fieldName: string,
    selectedId: any,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const raw = event.target.value;
    const filtered =  filterAllowedChars(raw);
    if (raw !== filtered) {
      setErrorTag(notificationMessage.ALLOWED_CHARACTERS_ERROR);
    } else {
      if (filtered) {
        setErrorTag(null);
      } else {
        setErrorTag("The tag name must be filled in!")
      }
    }
    setEditedTagDetails({
      ...editedTagDetails,
      name: filtered,
      id: selectedId,
      modified: true,
    });
  };

  const handleBlur = (type: "label" | "tag") => {
    const current= type === "label" ? (editedLabelDetails?.name ?? "")
      : (editedTagDetails?.name ?? "");
    const removedCharacters = removeInvalidCharacters(current);
    if (current !== removedCharacters) {
      if (type === "label") {
        setEditedLabelDetails((prev) => prev ? {...prev, name: removedCharacters} : prev);
      } else {
        setEditedTagDetails((prev) => prev ? {...prev, name: removedCharacters} : prev);
      }
    }
  }
  return (
    <>
      {userRole !== "Superadmin" ? (
        <AccessDeniedPage />
      ) : (
        <Box>
          <AppBar position="relative" sx={styles.dataBox}>
            <Toolbar sx={styles.toolBar}>
              <Box sx={styles.labelsBox}>
                <Typography component="div" sx={styles.headerContent}>
                  Labels: <b>{labels.length}</b>
                </Typography>
              </Box>
              <Box>
                <Typography component="div" sx={styles.headerContent}>
                  Tags: <b>{tags.length}</b>
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>
          <Paper sx={styles.formHolder}>
            <Box sx={styles.container}>
              <Box
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                }}
              >
                {driveConnection ? (
                  <Box sx={styles.wrapperGoogleDrive}>
                    <Typography
                      component="div"
                      sx={[styles.headerContent, styles.googleDrive]}
                    >
                      System is connect to Randvast G-Suite.
                      <br /> If you want to connect to another drive, please
                      contact project's owner.
                    </Typography>
                    <Button
                      variant="contained"
                      sx={[styles.buttonAdd, styles.buttonDisconnect]}
                      startIcon={<DeleteIcon />}
                      onClick={handleRemoveConnection}
                    >
                      Disconnect
                    </Button>
                    <Tooltip
                      title={
                        "Clue: note, that connection to google-drive should make only G-suite owners! Please contact project owners before connect to drive."
                      }
                    >
                      <IconButton>
                        <Info sx={{ color: "#495057" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box sx={styles.wrapperGoogleDrive}>
                    <Typography
                      component="div"
                      sx={[styles.headerContent, styles.googleDrive]}
                    >
                      Google drive is not connected yet.
                    </Typography>
                    <Button
                      variant="contained"
                      sx={styles.buttonAdd}
                      startIcon={<AddCircleOutlineOutlinedIcon />}
                      onClick={() => handleAddConnection()}
                    >
                      Add connection
                    </Button>
                    <Tooltip
                      title={
                        "Clue: note, that connection to google-drive should make only G-suite owners! Please contact project owners before connect to drive."
                      }
                    >
                      <IconButton>
                        <Info sx={{ color: "#495057" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                }}
              >
                <Grid
                  container
                  flexDirection={{ xs: "column", sm: "row" }}
                  columnSpacing={{ xs: 2, sm: 3, md: 4 }}
                  justifyContent="flex-end"

                >
                  <Grid
                    container
                    // size="grow"
                    size={{xs: 12, sm: 6}}
                    flexDirection={{ xs: "column" }}
                    rowSpacing={2}
                    sx={{ marginBottom: { xs: 4, sm: 0 }, minWidth: 0 }}
                  >
                    <Grid sx={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        variant="contained"
                        sx={styles.buttonCreate}
                        onClick={() =>
                          eventEmitter.emit(
                            types.openLabelTagCreateModal,
                            "labelCreate"
                          )
                        }
                      >
                        Create label
                      </Button>
                    </Grid>
                    {/*<Grid*/}
                    {/*  flexGrow={1}*/}
                    {/*>*/}
                    <Paper sx={styles.containerLabels}>
                      <List sx={styles.listContainer}>
                        <Box component="li" sx={{height: "0.5rem"}}/>
                        {labels.map(({ id, name }) => (
                          <ListItem
                            key={id}
                            sx={styles.listItem}
                            secondaryAction={
                              <Box
                                sx={{
                                  width: 88,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                { editModeLabel.isEdit &&
                                editModeLabel.id === id ? (
                                  <>
                                    <IconButton
                                      edge="end"
                                      aria-label="done"
                                      onClick={() =>
                                        handleSetEditMode("saveLabelEdit", id)
                                      }
                                    >
                                      <DoneIcon sx={styles.iconButton} />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      aria-label="close"
                                      onClick={() =>
                                        handleSetEditMode("closeLabelEdit", id)
                                      }
                                    >
                                      <CloseIcon sx={styles.iconButton} />
                                    </IconButton>
                                  </>
                                ) : (
                                  <>
                                    <IconButton
                                      edge="end"
                                      aria-label="edit"
                                      onClick={() =>
                                        handleSetEditMode("openLabelEdit", id)
                                      }
                                    >
                                      <EditIcon
                                        sx={[styles.iconButton, styles.iconList]}
                                      />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      aria-label="delete"
                                      onClick={() =>
                                        eventEmitter.emit(
                                          types.openDeleteLabelTagModal,
                                          "labelDelete",
                                          id
                                        )
                                      }
                                    >
                                      <DeleteIcon
                                        sx={[styles.iconButton, styles.iconList]}
                                      />
                                    </IconButton>
                                  </>
                                )
                                }
                              </Box>
                            }
                          >
                            {editModeLabel.isEdit && editModeLabel.id === id ? (
                              <TextField
                                variant="standard"
                                name="name"
                                size="small"
                                fullWidth
                                autoComplete="off"
                                sx={{
                                  minWidth: 0,
                                }}
                                error={Boolean(errorLabel)}
                                onChange={(e) =>
                                  handleChangeLabel("name", id, e)
                                }
                                onBlur={() => handleBlur("label")}
                                value={editedLabelDetails?.name ?? name}
                                // defaultValue={name}
                                // {/*{...(errorLabel && { helperText: })}*/}
                                helperText={errorLabel}
                              />
                            ) : (
                              <ListItemText primary={name} />
                            )}
                          </ListItem>
                        ))}
                        <Box component="li" sx={{height: "0.5rem"}}/>
                      </List>
                    </Paper>
                    {/*</Grid>*/}
                  </Grid>
                  <Grid
                    container
                    // size="grow"
                    size={{xs: 12, sm: 6}}
                    flexDirection={{ xs: "column" }}
                    rowSpacing={2}
                    sx={{ minWidth: 0 }}
                  >
                    <Grid sx={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        variant="contained"
                        sx={styles.buttonCreate}
                        onClick={() =>
                          eventEmitter.emit(
                            types.openLabelTagCreateModal,
                            "tagCreate"
                          )
                        }
                      >
                        Create tag
                      </Button>
                    </Grid>
                    {/*<Grid*/}
                    {/*   flexGrow={1}*/}
                    {/*  // direction="row"*/}
                    {/*>*/}
                    <Paper sx={styles.containerLabels}>
                      <List sx={styles.listContainer}>
                        <Box component="li" sx={{height: "0.5rem"}}/>
                        {tags.map(({ id, name }) => (
                          <ListItem
                            key={id}
                            sx={styles.listItem}
                            secondaryAction={
                              <Box
                                sx={{
                                  width: 88,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                {editModeTag.isEdit && editModeTag.id === id ? (
                                <>
                                  <IconButton
                                    edge="end"
                                    aria-label="done"
                                    onClick={() =>
                                      handleSetEditMode("saveTagEdit", id)
                                    }
                                  >
                                    <DoneIcon sx={styles.iconButton} />
                                  </IconButton>
                                  <IconButton
                                    edge="end"
                                    aria-label="close"
                                    onClick={() =>
                                      handleSetEditMode("closeTagEdit", id)
                                    }
                                  >
                                    <CloseIcon sx={styles.iconButton} />
                                  </IconButton>
                                </>
                              ) : (
                                <>
                                  <IconButton
                                    edge="end"
                                    aria-label="edit"
                                    onClick={() =>
                                      handleSetEditMode("openTagEdit", id)
                                    }
                                  >
                                    <EditIcon
                                      sx={[styles.iconButton, styles.iconList]}
                                    />
                                  </IconButton>
                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={() =>
                                      eventEmitter.emit(
                                        types.openDeleteLabelTagModal,
                                        "tagDelete",
                                        id
                                      )
                                    }
                                  >
                                    <DeleteIcon
                                      sx={[styles.iconButton, styles.iconList]}
                                    />
                                  </IconButton>
                                </>
                              )
                              }
                              </Box>
                            }
                          >
                            {editModeTag.isEdit && editModeTag.id === id ? (
                              <TextField
                                name="name"
                                variant="standard"
                                size="small"
                                fullWidth
                                autoComplete="off"
                                sx={{
                                  minWidth: 0,
                                }}
                                error={Boolean(errorTag)}
                                onChange={(e) => handleChangeTag("name", id, e)}
                                onBlur={() => handleBlur("tag")}
                                value={editedTagDetails.name ?? name}
                                // defaultValue={name}
                                // {/*{...(errorTag && { helperText: "The tag name must be filled in!"})}*/}
                                helperText={errorTag}
                              />
                            ) : (
                              <ListItemText primary={name} />
                            )}
                          </ListItem>
                        ))}
                        <Box component="li" sx={{height: "0.5rem"}}/>
                      </List>
                    </Paper>
                    {/*</Grid>*/}
                  </Grid>
                </Grid>



              </Box>

            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
};

function mapStateToProps(state: RootState) {
  const { labelsList, tagsList, driveConnectionCheck } =
    selectSuperAdmin(state);
  const { userRole } = selectUser(state);
  return { labelsList, tagsList, driveConnectionCheck, userRole };
}

export default connect(mapStateToProps)(SuperAdmin);
